import React, { type FC } from 'react';

import { Box, Container, CtaWithSSO, VisibleVideo, Heading, Text } from '@lucid/components';
import { mqMin } from '@lucid/theme';

import type { Lucid } from 'types/types';

interface ABTestNewHeroLayoutProps {
  i18nData?: Lucid.I18n.DataEdges;
  cta?: Lucid.Directus.Generated.Links;
}

const heroData = {
  heading: 'Visualize and build anything as a team',
  text: 'Lucidchart gives you easy-to-use features for advanced diagramming and real-time collaboration',
};

const ABTestNewHeroLayout: FC<ABTestNewHeroLayoutProps> = ({ i18nData, cta }) => {
  const { heading, text } = heroData;

  return (
    <Box
      css={theme => ({
        padding: `${theme.space.s64}px 0 ${theme.space.s40}px`,
        background: theme.colors.orange[10],
        overflow: 'hidden',
      })}
    >
      <Container
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          columnGap: theme.space.s64,
          rowGap: theme.space.s40,
          alignItems: 'center',
          [mqMin.tablet]: {
            flexDirection: 'row',
          },
        })}
      >
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
            [mqMin.tablet]: { textAlign: 'left', alignItems: 'start', flex: '1 0 420px' },
          }}
        >
          <Heading
            color="orange.80"
            css={theme => ({ marginBottom: theme.space.s24, [mqMin.computer]: { display: 'none' } })}
            variant="hero2XL900"
          >
            {heading}
          </Heading>
          <Heading
            color="orange.80"
            css={theme => ({ marginBottom: theme.space.s24, display: 'none', [mqMin.computer]: { display: 'block' } })}
            variant="hero3XL900"
          >
            {heading}
          </Heading>
          <Text color="orange.80" css={theme => ({ marginBottom: theme.space.s40 })} variant="paragraphM400">
            {text}
          </Text>
          <CtaWithSSO i18nData={i18nData} linkText={cta?.default_link_text} linkUrl={cta?.final_url} />
        </Box>
        <VisibleVideo
          shouldLoop
          css={{
            width: '150vw',
            maxWidth: 'max(620px, 100%)',
            alignSelf: 'flex-start',
            [mqMin.tabletSmall]: { width: '100%', maxWidth: '1020px' },
            [mqMin.tablet]: { flex: '1 0 75vw' },
          }}
          i18nData={i18nData}
          src="https://cdn-cashy-static-assets.lucidchart.com/marketing/flowchart/Landing_Page_Flowchart_1080.mp4"
        />
      </Container>
    </Box>
  );
};

export default ABTestNewHeroLayout;
